import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  useGenerateGeoSchemeMutation,
  usePatchGeoDeviationsMutation,
  useUploadGeoVersionMutation,
} from '@shared/api';
import type { GeoOverride } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import {
  compactOverrides,
  DeviationTable,
  ErrorText,
  FieldLabel,
  Hint,
  isEmptyOverride,
  LinkButton,
  Muted,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  Stack,
  TextArea,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useStepDirty, useWizard } from './wizard';

const ACCEPT_CAD = '.dxf,.pdf';
const GENERATE_FROM = new Set(['draft', 'generated', 'rejected']);

const FileLine = styled.div`
  font-size: 13.5px;

  b {
    font-weight: 600;
  }
`;

/** Шаг 9. Корректировки: правка сопоставления, исправленный файл из CAD, перегенерация. */
export function StepCorrections() {
  const { scheme, readOnly, goTo } = useWizard();
  const report = scheme.deviations;
  const overrides = useDraft<GeoOverride[]>(report?.overrides ?? []);
  useStepDirty('overrides', overrides.dirty && !readOnly);
  const [patch] = usePatchGeoDeviationsMutation();
  const [upload] = useUploadGeoVersionMutation();
  const [generate] = useGenerateGeoSchemeMutation();
  const saving = useAction();
  const uploading = useAction();
  const gen = useAction();
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');

  const surveyNames = useMemo(
    () => Array.from(new Set(scheme.survey.points.map((p) => p.name).filter(Boolean))),
    [scheme.survey.points],
  );
  const canGenerate = !readOnly && GENERATE_FROM.has(scheme.status);

  /** Правка строки: заменить на месте; «всё по умолчанию» — убрать запись. */
  function onOverride(next: GeoOverride) {
    overrides.update((prev) => {
      if (isEmptyOverride(next)) return prev.filter((o) => o.element_id !== next.element_id);
      const exists = prev.some((o) => o.element_id === next.element_id);
      return exists
        ? prev.map((o) => (o.element_id === next.element_id ? next : o))
        : [...prev, next];
    });
  }

  // Исключить элемент — только с причиной (сервер без неё не примет).
  const noReason = (report?.items ?? [])
    .filter((d) =>
      overrides.draft.some((o) => o.element_id === d.element_id && o.excluded && !o.note?.trim()),
    )
    .map((d) => d.label);

  async function save(): Promise<boolean> {
    if (noReason.length > 0) {
      saving.setError(
        `Укажите причину исключения в примечании: ${noReason.join(', ')} — она попадёт в журнал и в контроль перед передачей.`,
      );
      return false;
    }
    const ok = await saving.run(
      () => patch({ id: scheme.id, overrides: compactOverrides(overrides.draft) }).unwrap(),
      'Корректировки не сохранены.',
    );
    if (ok) overrides.reset();
    return ok;
  }

  async function sendFile() {
    if (!file) return;
    const ok = await uploading.run(
      () => upload({ id: scheme.id, file, comment: comment.trim() }).unwrap(),
      'Файл не загрузился.',
    );
    if (ok) {
      setFile(null);
      setComment('');
    }
  }

  return (
    <StepShell
      n={9}
      lead="Работа возможна полностью без AutoCAD: исключите элемент, сопоставьте точку вручную, поправьте съёмку — и сгенерируйте схему заново. DXF — для тех, кто хочет допилить чертёж в CAD и загрузить исправленный файл новой версией."
      save={
        report
          ? {
              dirty: overrides.dirty && !readOnly,
              busy: saving.busy,
              error: saving.error,
              onSave: save,
              onReset: overrides.reset,
            }
          : undefined
      }
    >
      <Panel aria-labelledby="geo-corr-title">
        <PanelHead>
          <PanelTitle id="geo-corr-title">Корректировки отклонений</PanelTitle>
          {!readOnly && (
            <LinkButton type="button" onClick={() => goTo(6, 'geo-survey')}>
              Править точки съёмки (шаг 6) →
            </LinkButton>
          )}
        </PanelHead>
        {report ? (
          <Stack $gap={3}>
            <Hint>
              Ручное сопоставление и исключения пересчитывают отклонения и пишутся в журнал. Имя
              точки — из съёмки, подсказки появятся при вводе.
            </Hint>
            <DeviationTable
              report={report}
              edit={
                readOnly
                  ? undefined
                  : { overrides: overrides.draft, onChange: onOverride, surveyNames }
              }
            />
          </Stack>
        ) : (
          <Muted>Сначала рассчитайте отклонения на шаге 7.</Muted>
        )}
      </Panel>

      {!readOnly && (
        <Panel aria-labelledby="geo-cad-title">
          <PanelHead>
            <PanelTitle id="geo-cad-title">Исправленный файл из CAD</PanelTitle>
          </PanelHead>
          <Stack $gap={3}>
            <Hint>
              Скачайте DXF на шаге 8, поправьте в AutoCAD, nanoCAD или BricsCAD и загрузите сюда —
              получится новая версия с пометкой «из CAD». Можно загрузить и PDF.
            </Hint>
            <FileDropZone
              accept={ACCEPT_CAD}
              compact
              disabled={uploading.busy}
              primary={file ? 'Выбрать другой файл' : 'Исправленный DXF или PDF'}
              hint="или нажмите, чтобы выбрать файл"
              onFile={setFile}
            />
            {file && (
              <>
                <FileLine>
                  Файл: <b>{file.name}</b>
                </FileLine>
                <FieldLabel>
                  Комментарий к версии
                  <TextArea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Что исправлено"
                  />
                </FieldLabel>
                <Row $gap={2}>
                  <Button
                    type="button"
                    $variant="primary"
                    onClick={() => void sendFile()}
                    disabled={uploading.busy}
                  >
                    {uploading.busy ? 'Загружаем…' : 'Загрузить новой версией'}
                  </Button>
                  <Button
                    type="button"
                    $variant="ghost"
                    onClick={() => {
                      setFile(null);
                      setComment('');
                    }}
                    disabled={uploading.busy}
                  >
                    Отмена
                  </Button>
                </Row>
              </>
            )}
            {uploading.error && <ErrorText role="alert">{uploading.error}</ErrorText>}
          </Stack>
        </Panel>
      )}

      {canGenerate && (
        <Panel aria-labelledby="geo-regen-title">
          <PanelHead>
            <PanelTitle id="geo-regen-title">Сгенерировать заново</PanelTitle>
            <Button
              type="button"
              $variant="secondary"
              onClick={() =>
                void gen.run(() => generate(scheme.id).unwrap(), 'Схема не сформирована.')
              }
              disabled={gen.busy || overrides.dirty}
            >
              {gen.busy ? 'Формируем…' : 'Сгенерировать новую версию'}
            </Button>
          </PanelHead>
          <Stack $gap={2}>
            <Hint>
              После корректировок выпустите новую версию и передайте её в ОСНОВА.Инженер на шаге 8.
            </Hint>
            {overrides.dirty && <Muted>Сначала сохраните корректировки.</Muted>}
            {gen.error && <ErrorText role="alert">{gen.error}</ErrorText>}
          </Stack>
        </Panel>
      )}
    </StepShell>
  );
}
